import React, { useEffect, useState } from "react";
import s from './wordDetail.module.css';
// Assuming your apiMiddleware also exports a postData function
import { getData, postData } from '../middleware/apiMiddleware';
import { useNavigate, useParams } from "react-router-dom";

// A simple helper to get the full name for a part of speech abbreviation
const getPartOfSpeech = (posAbbreviation) => {
  const posMap = {
    'N': 'Noun', 'PROPN': 'Proper Noun', 'V': 'Verb',
    'ADJ': 'Adjective', 'ADV': 'Adverb', 'PREP': 'Preposition',
    'CONJ': 'Conjunction',
  };
  return posMap[posAbbreviation] || posAbbreviation;
};

export default function WordDetail() {
  const navigate = useNavigate();
  const params = useParams();
  const wordId = params.wordId;

  // State for the main data, loading, and errors
  const [wordData, setWordData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editableWordData, setEditableWordData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Effect to fetch data whenever the wordId from the URL changes
  useEffect(() => {
    if (!wordId) {
      setLoading(false);
      setError("No word ID provided.");
      return;
    }

    // --- FIX: Correctly use template literal for wordId ---
    const apiUrl = `vocabApi/getWord?wordId=${wordId}`;

    setLoading(true);
    setError(null);

    getData(apiUrl, navigate)
      .then(res => {
        if (res && res.word) {
          setWordData(res.word);
          // Initialize editable data as a deep copy for editing
          setEditableWordData(JSON.parse(JSON.stringify(res.word)));
        } else {
          throw new Error("Invalid data structure received from API.");
        }
      })
      .catch(err => {
        console.error("Failed to fetch vocabulary:", err);
        setError("Could not load data. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });

  }, [wordId, navigate]);

  // --- Handlers for Edit Mode ---

  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null); // Clear previous save errors
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset any changes by deep copying from original data
    setEditableWordData(JSON.parse(JSON.stringify(wordData)));
  };

  const handleSave = () => {
    setIsSaving(true);
    setSaveError(null);

    const payload = {
      word: editableWordData.word,
      // The API expects the definition object to be stringified
      definitions: editableWordData.vocab.definitions
    };

    postData('vocabApi/update_vocab_definition', payload, navigate)
      .then(() => {
        // On success, update the main data and exit edit mode
        setWordData(editableWordData);
        setIsEditing(false);
      })
      .catch(err => {
        console.error("Failed to save data:", err);
        setSaveError("Failed to save changes. Please try again.");
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // Handles changes in any input field for a specific definition
  const handleDefinitionChange = (e, index, field) => {
    const { value } = e.target;
    const updatedDefinitions = [...editableWordData.vocab.definitions.definitions];
    updatedDefinitions[index] = { ...updatedDefinitions[index], [field]: value };

    setEditableWordData(prev => ({
      ...prev,
      vocab: {
        ...prev.vocab,
        definitions: {
          ...prev.vocab.definitions,
          definitions: updatedDefinitions
        }
      }
    }));
  };

  const handleAddDefinition = () => {
    const newDefinition = {
      // Create a new unique 'i' for the key and for the backend
      i: Math.max(0, ...editableWordData.vocab.definitions.definitions.map(d => d.i)) + 1,
      pos: 'N', ipa_UK: '', ipa_US: '', def: '', chi_zh: '', chi_cn: '',
      example: '', example_translation_zh: '', example_translation_cn: ''
    };

    const updatedDefinitions = [...editableWordData.vocab.definitions.definitions, newDefinition];

    setEditableWordData(prev => ({
      ...prev,
      vocab: {
        ...prev.vocab,
        definitions: {
          ...prev.vocab.definitions,
          definitions: updatedDefinitions
        }
      }
    }));
  };

  const handleRemoveDefinition = (indexToRemove) => {
    const updatedDefinitions = editableWordData.vocab.definitions.definitions.filter((_, index) => index !== indexToRemove);

    setEditableWordData(prev => ({
        ...prev,
        vocab: {
          ...prev.vocab,
          definitions: {
            ...prev.vocab.definitions,
            definitions: updatedDefinitions
          }
        }
      }));
  };


  if (loading) return <div className={s.container}><p>Loading word details...</p></div>;
  if (error) return <div className={s.container}><p className={s.error}>{error}</p></div>;
  if (!wordData) return <div className={s.container}><p>Word not found.</p></div>;

  // Determine which data to display (original or editable)
  const displayData = isEditing ? editableWordData : wordData;

  return (
    <div className={s.container}>
      <div className={s.headerContainer}>
        <h1 className={s.wordTitle}>{displayData.word}</h1>
        <div className={s.headerControls}>
          {isEditing ? (
            <>
              <button onClick={handleSave} className={`${s.button} ${s.saveButton}`} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={handleCancel} className={`${s.button} ${s.cancelButton}`} disabled={isSaving}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={handleEdit} className={`${s.button} ${s.editButton}`}>Edit</button>
            </>
          )}
        </div>
      </div>
      {saveError && <p className={s.error}>{saveError}</p>}

      <div className={s.definitionsContainer}>
        {displayData.vocab.definitions.definitions.map((defItem, index) => (
          <div key={defItem.i} className={s.definitionCard}>
            {isEditing ? (
              // --- EDITING VIEW ---
              <>
                <div className={s.editGrid}>
                  <label>Part of Speech</label><input type="text" className={s.editInput} value={defItem.pos} onChange={(e) => handleDefinitionChange(e, index, 'pos')} />
                  <label>IPA (UK)</label><input type="text" className={s.editInput} value={defItem.ipa_UK} onChange={(e) => handleDefinitionChange(e, index, 'ipa_UK')} />
                  <label>IPA (US)</label><input type="text" className={s.editInput} value={defItem.ipa_US} onChange={(e) => handleDefinitionChange(e, index, 'ipa_US')} />
                </div>
                <div className={s.editSection}>
                  <label>Definition</label>
                  <textarea className={s.editTextArea} value={defItem.def} onChange={(e) => handleDefinitionChange(e, index, 'def')} />
                  <label>中文 (Simplified)</label>
                  <input type="text" className={s.editInput} value={defItem.chi_cn} onChange={(e) => handleDefinitionChange(e, index, 'chi_cn')} />
                  <label>中文 (Traditional)</label>
                  <input type="text" className={s.editInput} value={defItem.chi_zh} onChange={(e) => handleDefinitionChange(e, index, 'chi_zh')} />
                </div>
                 <div className={s.editSection}>
                    <label>Example Sentence</label>
                    <textarea className={s.editTextArea} value={defItem.example} onChange={(e) => handleDefinitionChange(e, index, 'example')} />
                    <label>Example Translation (CN)</label>
                    <textarea className={s.editTextArea} value={defItem.example_translation_cn} onChange={(e) => handleDefinitionChange(e, index, 'example_translation_cn')} />
                 </div>
                 <button onClick={() => handleRemoveDefinition(index)} className={`${s.button} ${s.removeButton}`}>
                    Remove Definition
                 </button>
              </>
            ) : (
              // --- DISPLAY VIEW (Original Code) ---
              <>
                <div className={s.posAndIpa}>
                  <span className={s.partOfSpeech}>{getPartOfSpeech(defItem.pos)}</span>
                  {defItem.ipa_UK && <span className={s.ipa}>UK: /{defItem.ipa_UK}/</span>}
                  {defItem.ipa_US && <span className={s.ipa}>US: /{defItem.ipa_US}/</span>}
                </div>
                <div className={s.definitionSection}>
                  <p className={s.definitionText}>{defItem.def}</p>
                  <p className={s.translationText}>
                    <strong>中文:</strong> {defItem.chi_cn}
                    {defItem.chi_cn !== defItem.chi_zh && ` / ${defItem.chi_zh}`}
                  </p>
                </div>
                {defItem.example && (
                  <div className={s.exampleBlock}>
                    <p className={s.exampleText}><em>"{defItem.example}"</em></p>
                    <p className={s.exampleTranslation}><em>{defItem.example_translation_cn}</em></p>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {isEditing && (
            <button onClick={handleAddDefinition} className={`${s.button} ${s.addButton}`}>
                + Add New Definition
            </button>
        )}
      </div>
    </div>
  );
}